import { PersonalDetailsCompanyDetailsData } from 'policy-issuing/personal-details/views/company-details';
import { CompanyPolicyholderForm } from 'policy-issuing/personal-details/views/company-policyholder-form';
import { ContactDetailsData } from 'policy-issuing/personal-details/views/contact-details';
import { PersonalDetailsIdentificationData } from 'policy-issuing/personal-details/views/identification';
import { IndividualPolicyholderForms } from 'policy-issuing/personal-details/views/individual-policyholder-forms';
import {
  incomingCellphoneCountryCodeToState,
  PersonalDetailsInputs,
} from 'policy-issuing/personal-details/views/personal-details';
import { PersonalAddressData } from 'policy-issuing/personal-details/views/physical-address-section';
import { getPolicyholder } from 'policyholder/actions/get-policyholder';
import { upsertPolicyholder } from 'policyholder/actions/upsert-policyholder';
import { updatePolicyholder } from 'policyholder/actions/update-policyholder';
import { Address, Policyholder, PolicyholderEntityType } from 'policyholder/domain/policyholder';
import { IdentificationType } from 'policyholder/domain/policyholder-identification';
import { ProductModule } from 'product-modules/domain/product-module';
import React, { useState, useMemo } from 'react';
import { FormWrapperStyle } from 'rootstrap/components-old/root-schema-form/root-schema-form';
import { LongButtonSuccess } from 'rootstrap/components/button/styles';
import { ErrorAlert } from 'rootstrap/components/error-alert';
import { ActiveElement } from 'rootstrap/components/forms/new-fields/utils';
import NewSpinner, { AnimationTypes, SpinnerSize } from 'rootstrap/components/spinner/new-spinner';
import { useEmbedParamsContext } from 'shared/embed-params-context';
import { usePromise, usePromiseLazy } from 'shared/hooks/promise';
import { EmbeddedConfigSection } from 'site-config';
import { useSiteConfigContext } from 'style-context';
import { AuthTypes } from 'shared/api';
import { ApiError } from 'shared/api';
import { jwtDecode } from 'jwt-decode';

interface Props {
  policyholder: Policyholder;
  productModule: ProductModule;
  setPolicyholder: (policyholder: Policyholder) => void;
  afterPolicyholderUpdated: () => void;
}

export const PolicyholderManagement = (props: Props) => {
  const { policyholder, productModule } = props;
  const { embedParams } = useEmbedParamsContext();
  const { auth, environment, organizationId } = embedParams;
  const { siteConfig } = useSiteConfigContext();

  const fetchifyAutoCompleteEnabled =
    siteConfig?.[EmbeddedConfigSection.PersonalDetails].displayOptionalSections.fetchifyAutoComplete;

  const isCompleted = false;
  const isPolicyholderTypeFieldDisplayed = false;
  const isCompany = policyholder?.type === PolicyholderEntityType.Company;
  const [addressOptIn, setAddressOptIn] = useState(
    Boolean(
      policyholder.address?.line1 ||
        policyholder.address?.suburb ||
        policyholder.address?.city ||
        policyholder.address?.country,
    ),
  );
  const [companyDetailsIsValid, setCompanyDetailsIsValid] = useState<boolean>(false);
  const [identificationIsValid, setIdentificationIsValid] = useState<boolean>(false);
  const [contactDetailsIsValid, setContactDetailsIsValid] = useState<boolean>(false);
  const [addressDetailsIsValid, setAddressDetailsIsValid] = useState<boolean>(false);
  const [policyholderIsValid, setPolicyholderIsValid] = useState<boolean>(false);

  const disableIdEdit = useMemo(() => {
    if (auth.type !== AuthTypes.JWT || !auth.token) {
      return true;
    }
    try {
      const decoded = jwtDecode<{ policyholderIdAuth?: unknown }>(auth.token);
      return !Boolean(decoded.policyholderIdAuth);
    } catch (e) {
      return true;
    }
  }, [auth.type, auth.token]);

  usePromise(async () => {
    if (policyholder.type === PolicyholderEntityType.Individual) {
      setPolicyholderIsValid(identificationIsValid && contactDetailsIsValid && addressDetailsIsValid);
    } else {
      setPolicyholderIsValid(companyDetailsIsValid && addressDetailsIsValid);
    }
  }, [identificationIsValid, contactDetailsIsValid, addressDetailsIsValid, companyDetailsIsValid]);

  const { execute, error, isLoading } = usePromiseLazy(async () => {
    const line1 = addressDetailsSectionData?.addressLine1 || '';
    const line2 = addressDetailsSectionData?.addressLine2 || '';
    const suburb = addressDetailsSectionData?.suburb || '';
    const city = addressDetailsSectionData?.city || '';
    const areaCode = addressDetailsSectionData?.areaCode || '';
    const country = addressDetailsSectionData?.country || '';
    const geoCoordinatesLatitude = addressDetailsSectionData?.geoCoordinatesLatitude || '';
    const geoCoordinatesLongitude = addressDetailsSectionData?.geoCoordinatesLongitude || '';
    const googlePlaceId = addressDetailsSectionData?.googlePlaceId;
    const isAddressRequired = Boolean(line1 || suburb || city || country || areaCode);

    // Build shared update payload
    const dataPayload = {
      firstName: identificationSectionData?.firstName,
      lastName: identificationSectionData?.lastName,
      identification: {
        country:
          identificationSectionData?.idType === IdentificationType.Id
            ? 'ZA'
            : identificationSectionData?.identificationCountry,
        type: identificationSectionData?.idType,
        number: identificationSectionData?.identificationNumber,
        expirationDate: identificationSectionData?.identificationExpirationDate,
      },
      gender: identificationSectionData?.gender,
      dateOfBirth:
        identificationSectionData?.idType !== IdentificationType.Id
          ? identificationSectionData?.dateOfBirth
          : undefined,
      email: contactDetailsSectionData?.email,
      cellphone: contactDetailsSectionData?.cellphone
        ? {
            countryCode: contactDetailsSectionData.cellphone.countryCode,
            number: contactDetailsSectionData.cellphone.number,
            internationalNumber: contactDetailsSectionData.cellphone.number,
          }
        : undefined,
      address:
        fetchifyAutoCompleteEnabled && fetchifyData
          ? {
              ...fetchifyData,
              line2,
              suburb,
            }
          : addressDetailsSectionData && isAddressRequired && addressOptIn
          ? {
              line1,
              line2,
              suburb,
              city,
              areaCode,
              country,
              geoCoordinatesLatitude,
              geoCoordinatesLongitude,
              googlePlaceId,
            }
          : undefined,
      registrationNumber: companyDetailsSectionData?.registrationNumber,
      companyName: companyDetailsSectionData?.companyName,
      companyWebsiteUrl: companyDetailsSectionData?.companyWebsiteUrl,
      contactPosition: companyDetailsSectionData?.contactPosition,
      subsidiaryCompanies: companyDetailsSectionData?.subsidiaryCompanies,
      dateOfEstablishment: companyDetailsSectionData?.dateOfEstablishment,
    };

    // Choose and call the correct action
    const policyholder = disableIdEdit
      ? await upsertPolicyholder({ isCompany, environment, auth, data: dataPayload, organizationId })
      : await updatePolicyholder({
          isCompany,
          environment,
          auth,
          organizationId,
          policyholderId: props.policyholder.policyholderId,
          data: dataPayload,
        });

    if (policyholder) {
      const updatedPolicyholder = await getPolicyholder({ auth, environment, organizationId });
      setPolicyholderState({ policyholder: updatedPolicyholder });
      props.setPolicyholder(updatedPolicyholder);
      props.afterPolicyholderUpdated();
    }
  }, []);

  // Override 409 conflict to a friendly message
  const conflictMessage = 'A policyholder with that ID already exists.';
  const displayError =
    error instanceof ApiError && error.type.includes('conflict')
      ? new ApiError({ message: conflictMessage, type: error.type })
      : error;

  const [activeElement, setActiveElement] = useState<ActiveElement>({
    elementId: isCompany
      ? PersonalDetailsInputs.RegistrationNumber
      : !isCompleted
      ? isPolicyholderTypeFieldDisplayed
        ? PersonalDetailsInputs.PolicyholderType
        : PersonalDetailsInputs.FirstName
      : '',
  });

  const setPolicyholderState = (params: { policyholder?: Policyholder }) => {
    const {
      FirstName,
      LastName,
      IdType,
      IdentificationNumber,
      IdentificationCountry,
      DateOfBirth,
      IdentificationExpirationDate,
      Gender,
      PolicyholderType,
      RegistrationNumber,
    } = PersonalDetailsInputs;

    const { policyholder } = params;
    const idType = policyholder?.identification?.type;

    const isTypeIsPassport = idType === IdentificationType.Passport;

    const existingPolicyholderExpirationDate = policyholder?.identification?.expirationDate;

    const gender = policyholder?.gender;
    const dateOfBirth = policyholder?.dateOfBirth;

    return {
      [FirstName]: policyholder?.firstName,
      [LastName]: policyholder?.lastName,
      [IdType]: idType,
      [IdentificationNumber]: policyholder?.identification?.number,
      [IdentificationCountry]: policyholder?.identification?.country,
      [DateOfBirth]: dateOfBirth,
      [PolicyholderType]: policyholder?.type,
      [IdentificationExpirationDate]: isTypeIsPassport ? existingPolicyholderExpirationDate : undefined,
      [Gender]: gender,
      [RegistrationNumber]: policyholder?.registrationNumber,
    };
  };

  const [identificationSectionData, setIdentificationSectionData] = useState<
    PersonalDetailsIdentificationData | undefined
  >(setPolicyholderState({ policyholder }));

  const [fetchifyData, setFetchifyData] = useState<Address | undefined>(policyholder?.address);

  const [contactDetailsSectionData, setContactDetailsSectionData] = useState<ContactDetailsData | undefined>({
    cellphone: incomingCellphoneCountryCodeToState(policyholder?.cellphone),
    email: policyholder?.email,
  });

  const [addressDetailsSectionData, setAddressDetailsSectionData] = useState<PersonalAddressData | undefined>({
    addressLine1: policyholder?.address?.line1,
    addressLine2: policyholder?.address?.line2,
    areaCode: policyholder?.address?.areaCode,
    city: policyholder?.address?.city,
    country: policyholder?.address?.country,
    suburb: policyholder?.address?.suburb,
    geoCoordinatesLatitude: policyholder?.address?.geoCoordinatesLatitude,
    geoCoordinatesLongitude: policyholder?.address?.geoCoordinatesLongitude,
    googlePlaceId: policyholder?.address?.googlePlaceId,
  });

  const [companyDetailsSectionData, setCompanyDetailsSectionData] = useState<
    PersonalDetailsCompanyDetailsData | undefined
  >({
    companyName: policyholder?.companyName,
    registrationNumber: policyholder?.registrationNumber,
    contactPosition: policyholder?.contactPosition,
    companyWebsiteUrl: policyholder?.companyWebsiteUrl,
    dateOfEstablishment: policyholder?.dateOfEstablishment,
    subsidiaryCompanies: policyholder?.subsidiaryCompanies,
  });

  return (
    <FormWrapperStyle>
      <ErrorAlert error={displayError} displayFieldError />
      {policyholder.type === PolicyholderEntityType.Company && (
        <CompanyPolicyholderForm
          productModule={productModule}
          fetchifyData={fetchifyData}
          setFetchifyData={setFetchifyData}
          isPolicyholderTypeFieldDisplayed={isPolicyholderTypeFieldDisplayed}
          personalDetailCompanyFormSectionData={companyDetailsSectionData}
          setCompanyDetailsIsValid={(companyDetailsIsValid) => setCompanyDetailsIsValid(companyDetailsIsValid)}
          setCompanyDetailsSectionData={(companyDetailsSectionData) =>
            setCompanyDetailsSectionData(companyDetailsSectionData)
          }
          policyholderPrefillValues={{}}
          setActiveElement={setActiveElement}
          activeElement={activeElement}
          isCompleted={isCompleted}
          addressDetailsSectionData={addressDetailsSectionData}
          setAddressDetailsIsValid={setAddressDetailsIsValid}
          setAddressDetailsSectionData={setAddressDetailsSectionData}
          contactDetailsSectionData={contactDetailsSectionData}
          setContactDetailsIsValid={setContactDetailsIsValid}
          setContactDetailsSectionData={setContactDetailsSectionData}
          identificationSectionData={identificationSectionData}
          setIdentificationIsValid={setIdentificationIsValid}
          setIdentificationSectionData={setIdentificationSectionData}
          addressOptIn={addressOptIn}
          setAddressOptIn={setAddressOptIn}
          policyholderLookupFormData={undefined}
          disableSteppedComponent={true}
          disableIdEdit={disableIdEdit}
        />
      )}
      {props.policyholder?.type === PolicyholderEntityType.Individual && (
        <>
          <IndividualPolicyholderForms
            activeElement={activeElement}
            isPolicyholderTypeFieldDisplayed={isPolicyholderTypeFieldDisplayed}
            identificationSectionData={identificationSectionData}
            productModule={productModule}
            isCompleted={isCompleted}
            contactDetailsSectionData={contactDetailsSectionData}
            addressOptIn={addressOptIn}
            addressDetailsSectionData={addressDetailsSectionData}
            setAddressOptIn={setAddressOptIn}
            setActiveElement={setActiveElement}
            fetchifyData={fetchifyData}
            setFetchifyData={setFetchifyData}
            setAddressDetailsIsValid={setAddressDetailsIsValid}
            setAddressDetailsSectionData={setAddressDetailsSectionData}
            setContactDetailsIsValid={setContactDetailsIsValid}
            setContactDetailsSectionData={setContactDetailsSectionData}
            setIdentificationIsValid={setIdentificationIsValid}
            setIdentificationSectionData={setIdentificationSectionData}
            policyholderPrefillValues={{}}
            policyholderLookupFormData={undefined}
            disableSteppedComponent={true}
            disableIdEdit={disableIdEdit}
          />
        </>
      )}
      <LongButtonSuccess
        id='save-policyholder-button'
        isLoading={isLoading}
        disabled={isLoading || !policyholderIsValid}
        siteConfig={siteConfig}
        onClick={execute}
      >
        {isLoading && (
          <span style={{ marginRight: '10px' }}>
            <NewSpinner animation={AnimationTypes.Border} size={SpinnerSize.sm} color='FFFFFF' />
          </span>
        )}
        Save
      </LongButtonSuccess>
    </FormWrapperStyle>
  );
};
